<template>
	<div class="container-chat-live">
		<div ref="scrollable" class="chat-item-list">
			<AppChatMessages :chat-messages="chatMessages" :writing="isLoading" />
			<div class="max-messages" v-show="maxMessages">
				<code>
					{{getString('max_messages')}}
					<br>
					{{getString('max_messages_alternatives')}}
				</code>
				<div class="row gap-10 pt-10">
					<div class="header-button" @click="clickShortcut('ticket')">
						<img :alt="getString('open_ticket')" :src="'https://img.qromo.io/img/icons/Icon-ticket-outline'+(userStore.darkMode ? '-dark' : '')+'.svg'"/>
						<div class="title-button">
							<p>{{ getString('open_ticket') }}</p>
						</div>
					</div>
					<div class="header-button" @click="clickShortcut('docs')">
						<img :alt="getString('show_wiki')" :src="'https://img.qromo.io/img/icons/Icon-wiky'+(userStore.darkMode ? '-dark' : '')+'.svg'"/>
						<div class="title-button">
							<p>{{ getString('show_wiki') }}</p>
						</div>
					</div>
					<div class="header-button" @click="clickShortcut('new-chat')">
						<img :alt="getString('new_chat')" :src="'https://img.qromo.io/img/icons/new-chat'+(userStore.darkMode ? '-dark' : '')+'.svg'"/>
						<div class="title-button">
							<p>{{ getString('new_chat') }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="chat-heading">
			<p class="title-chat">{{ getString('can_i_help_you') }}</p>
		</div>
		<div class="chat-inputs" v-show="!maxMessages">
			<div class="wrapper-input-text">
				<AppTextInput ref="main-input" :readonly="isLoading" class="main-input" textarea :value="typedMessage" :placeholder="getString('ask_a_question')" @changing-text="typingMessage"/>
				<AppButtonIcon :loading="isLoading" class="input-absolute" icon-name="icon-ai-white" prevent-change @click-icon="sendMessage" />
			</div>
		</div>
	</div>
</template>

<script>
import AppTextInput from "@/shared/components/AppTextInput.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useOpenAIChatsStore, useUserStore} from "@/console-new/stores";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppChatMessages from "@/shared/components/AppChatMessages.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let openAIChatsStore = useOpenAIChatsStore();
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		return {getString: languageStore.getString, userStore, openAIChatsStore, showNotification: notificationStore.showNotification};
	},
	name: "ChatLive",
	components: {AppLoader, AppButtonIcon, AppChatMessages, AppTextInput},
	props: {
		chats: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			typedMessage: null,
		}
	},
	computed: {
		isLoading() {
			return !!this.openAIChatsStore.loadingChats || !!this.openAIChatsStore.pendingResponse;
		},
		chatMessages() {
			if(!this.openAIChatsStore?.activeChat) {
				return [];
			}
			return this.openAIChatsStore.activeChat?.messages || [];
		},
		maxMessages() {
			return !this.isLoading && (this.chatMessages?.length || 0) >= 100;
		}
	},
	methods: {
		clickShortcut(shortcut) {
			if(this.isLoading) {
				return;
			}
			if(shortcut === 'ticket') {
				window.open("https://qromo-help.freshdesk.com/support/tickets/new", "_blank");
			} else if(shortcut === 'docs') {
				window.open("https://qromo-help.freshdesk.com/support/home", "_blank");
			} else {
				this.openAIChatsStore.newChat();
			}
		},
		typingMessage(v) {
			let maxMsgLength = 500;
			if(v?.length && v.length > maxMsgLength) {
				v = v.slice(0, maxMsgLength);
				let txtArea = this.$refs['main-input'].$el.querySelector('textarea');
				if(txtArea) {
					txtArea.value = v;
				}
			}
			this.typedMessage = v || '';
			this.$refs['main-input'].currentValue;
		},
		sendFirstMessage() {
			let msg = this.typedMessage;
			this.typedMessage = null;
			this.eraseInput();
			this.openAIChatsStore.createChat(msg, newChat => {
				if(!newChat) {
					this.showNotification("generic-fail");
				}
			});
		},
		sendMessage() {
			if(this.openAIChatsStore.loadingChats || !this.typedMessage?.length || this.openAIChatsStore.pendingResponse) {
				return;
			}
			if(!this.openAIChatsStore?.activeChat?.messages?.length) {
				this.sendFirstMessage();
			} else {
				let chatId = this.openAIChatsStore?.activeChat?.ai_chat_id;
				let msg = this.typedMessage;
				if(!chatId || !msg) {return}
				this.typedMessage = null;
				this.eraseInput();
				this.openAIChatsStore.sendMessage(chatId, msg, success => {
					if(!success) {
						this.showNotification("generic-fail");
					}
				});
			}
		},
		scrollChatToBottom() {
			this.$nextTick(() => {
				if(this.$refs['scrollable']?.scrollTo && this.$refs['scrollable']?.scrollHeight) {
					this.$refs['scrollable'].scrollTo(0,this.$refs['scrollable'].scrollHeight);
				}
			});
		},
		eraseInput() {
			if(this.$refs['main-input']?.$el) {
				let txtArea = this.$refs['main-input'].$el.querySelector('textarea');
				if(txtArea) {
					txtArea.value = null;
				}
			}
		},
		enterEventListener(ev) {
			if (
				ev.key === 'Enter' &&
				!ev.shiftKey &&
				!ev.ctrlKey &&
				!ev.altKey &&
				!ev.metaKey
			) {
				ev.preventDefault();
				this.sendMessage();
			}
		}
	},
	mounted() {
		let txtArea = this.$refs['main-input']?.$refs?.['input'];
		if(txtArea) {
			txtArea.addEventListener('keypress', this.enterEventListener)
		}
	},
	beforeUnmount() {
		let txtArea = this.$refs['main-input']?.$refs?.['input'];
		if(txtArea) {
			txtArea.removeEventListener('keypress', this.enterEventListener)
		}
	},
	watch: {
		'openAIChatsStore.activeChatId': function (newVal) {
			this.typedMessage = null;
			if(newVal) {
				this.eraseInput()
			}
			if(newVal && this.$refs['main-input']?.focus) {
				this.$refs['main-input'].focus();
			}
		},
		'chatMessages': function (newVal, oldVal) {
			this.scrollChatToBottom();
		},
	}
};
</script>

<style scoped lang="scss">
	.container-chat-live {
		display: flex;
		flex-direction: column;
		gap: 30px;
		.chat-heading {
			.title-chat {
				font-weight: 600;
				font-size: 24px;
				color: var(--color-text-1);
				line-height: normal;
				text-align: center;
			}
		}
		.chat-item-list {
			display: none;
			flex-direction: column;
			gap: 40px;
			height: calc(100% - 168px);
			overflow-y: auto;
			scrollbar-width: thin; /* thin | auto | none */
			scrollbar-color: var(--color-bg-primary-1) transparent; /* thumb track */
		}
		.chat-inputs {
			position: relative;
			padding: 1px;

			&::before{
				content: "";
				position: absolute;
				inset: 0;
				padding: 1px;
				border-radius: 13px;
				--angle:160deg;
				background: linear-gradient(var(--angle),var(--color-secondary) 0%,#E3216B 27%,#8F4DFE 63%,var(--color-primary) 100%);
				-webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
				-webkit-mask-composite: xor;
				mask-composite: exclude;
				pointer-events: none;
			}

			.wrapper-input-text {
				position: relative;
				.main-input {
					width: 100%;
				}
				:deep(.input-wrapper) {
					width: 100%;
					.input-element {
						background-color: var(--color-bg-primary-1);
						textarea {
							background-color: transparent;
							padding: 6px 12px 38px;
							height: 100px;
							min-height: 100px;
							max-height: 300px;
						}
					}
				}
				.input-absolute {
					position: absolute;
					bottom: 8px;
					right: 8px;
					/*&:deep(.btn-inner) {
						background: linear-gradient(160deg,var(--color-secondary) 0%,#E3216B 27%,#8F4DFE 63%,var(--color-primary) 100%);
					}*/
				}
			}

		}

		.max-messages {
			code {
				font-family: monospace;
			}

			.header-button {
				@include centered;
				gap: 10px;
				padding: 6px 10px;
				background-color: var(--color-bg-primary-1);
				border-radius: 10px;
				cursor: pointer;
				img {
					width: 15px;
					height: 15px;
				}
				.title-button {
					p {
						font-weight: 500;
						font-size: 14px;
						color: var(--color-primary);
					}
				}
			}
		}
	}
</style>